import { CircularProgress, TextField } from "@mui/material";
import { useDragLayer, useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import { useEffect, useMemo, useRef, useState } from "react";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";

import { Controller, useForm } from "react-hook-form";

import { Icon } from "@/components";

import { useAppSelector } from "@/redux/hooks";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";

import { ToDoStatus } from "../enum/ToDoStatus.enum";
import ToDoItem from "./ToDoItem";
import { useToDoSocket } from "../providers/SocketContext";
import {
	CreateTaskInfo,
	SubTaskInfo,
	TaskInfo,
} from "../../../TasksAndSubTasks/schema/tasks-and-subtasks";
import AssigneeModal from "../../../TasksAndSubTasks/components/AssigneeModal";

interface ToDoContainerProps {
	data: TaskInfo[] | SubTaskInfo[];
	showTaskDetails: (companyToDoProgressId: number) => void;
	isLoading: boolean;
	icon: string;
	status: ToDoStatus;
	showSubTaskDetails?: (subTaskId: number) => void;
	allItems: (TaskInfo | SubTaskInfo)[];
}

export default function ToDoContainer({
	data,
	isLoading,
	icon,
	showTaskDetails,
	showSubTaskDetails,
	status,
	allItems,
}: ToDoContainerProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);
	const { changeTaskStatus, changeSubTaskStatus } = useToDoSocket();
	const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
	const [droppedData, setDroppedData] = useState<TaskInfo | SubTaskInfo | null>(null);
	const [expandedTaskIds, setExpandedTaskIds] = useState<Set<number>>(new Set());

	const toggleExpanded = (taskId: number) => {
		setExpandedTaskIds((prev) => {
			const next = new Set(prev);

			if (next.has(taskId)) {
				next.delete(taskId);
			} else {
				next.add(taskId);
			}

			return next;
		});
	};

	const [{ isOverNewContainer }, drop] = useDrop({
		accept: ["TODO_ITEM", "TODO_SUB_ITEM"],
		canDrop: (item: { taskId?: number; subTaskId?: number; status: ToDoStatus }) => {
			if (status === ToDoStatus.DONE && item.status !== ToDoStatus.QA) {
				return false;
			}

			return true;
		},
		collect: (monitor) => ({
			isOverNewContainer:
				monitor.isOver() && monitor.canDrop() && monitor.getItem()?.status !== status,
		}),
		drop: (item: { taskId?: number; subTaskId?: number; status: ToDoStatus }, monitor) => {
			const type = monitor.getItemType();

			if (item?.status === status) {
				return;
			}

			if (status === ToDoStatus.QA) {
				const found = item.subTaskId
					? allItems.find((d) => !d.isTask && d.id === item.subTaskId) ?? null
					: allItems.find((d) => d.isTask && d.id === item.taskId) ?? null;

				setDroppedData(found);
				setIsAssigneeModalOpen(true);

				return;
			}

			if (type === "TODO_ITEM" && item.taskId) {
				changeTaskStatus(item.taskId, status);
			} else if (type === "TODO_SUB_ITEM" && item.subTaskId && item.taskId) {
				changeSubTaskStatus(item.taskId, item?.subTaskId, status);
			}
		},
	});

	const { draggingItem } = useDragLayer((monitor) => ({
		draggingItem: monitor.isDragging() ? monitor.getItem() : null,
	}));

	const isBlocked =
		!!draggingItem && status === ToDoStatus.DONE && draggingItem.status !== ToDoStatus.QA;

	const { user } = useAppSelector(authSelector);
	const { createTask } = useToDoSocket();

	const { control, handleSubmit, reset } = useForm<CreateTaskInfo>({
		defaultValues: {
			title: "",
			description: "",
			assigneeId: user?.id,
			status,
			complexityEstimate: 2,
		},
	});

	useEffect(() => {
		if (user?.id) {
			reset((prev) => ({ ...prev, assigneeId: user.id }));
		}
	}, [user?.id]);

	const containerRef = useRef<HTMLDivElement | null>(null);

	const onQuickAddSubmit = async (data: CreateTaskInfo) => {
		try {
			createTask(data);
			reset({
				title: "",
				description: "",
				assigneeId: user?.id,
				status,
				complexityEstimate: 2,
			});
		} catch (err) {
			console.error(err);
		} finally {
			setIsQuickAddShowing(false);
		}
	};

	const [isQuickAddShowing, setIsQuickAddShowing] = useState(false);

	const quickAddRef = useRef<HTMLDivElement | null>(null);

	const { data: users } = useGetAssigneesQuery();

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (quickAddRef.current && !quickAddRef.current.contains(e.target as Node)) {
				setIsQuickAddShowing(false);
			}
		};

		if (isQuickAddShowing) {
			document.addEventListener("mousedown", handleClickOutside);
			quickAddRef?.current?.focus();
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isQuickAddShowing]);

	useEffect(() => {
		if (isOverNewContainer && containerRef.current) {
			containerRef.current.scrollTop = 0;
		}
	}, [isOverNewContainer]);

	const groups = useMemo<{ mainTask: TaskInfo | null; subTasks: SubTaskInfo[] }[]>(() => {
		const result: { mainTask: TaskInfo | null; subTasks: SubTaskInfo[] }[] = [];
		const items = [...data];
		let i = 0;

		while (i < items.length) {
			const item = items[i];

			if (item.isTask) {
				const subTasks: SubTaskInfo[] = [];
				let j = i + 1;

				while (
					j < items.length &&
					!items[j].isTask &&
					(items[j] as unknown as SubTaskInfo).taskId === item.id
				) {
					subTasks.push(items[j] as unknown as SubTaskInfo);
					j++;
				}

				result.push({ mainTask: item as unknown as TaskInfo, subTasks });
				i = j;
			} else {
				const targetTaskId = (items[i] as unknown as SubTaskInfo).taskId;
				const matched: SubTaskInfo[] = [];
				const unmatched: (typeof items)[number][] = [];

				while (i < items.length && !items[i].isTask) {
					const sub = items[i] as unknown as SubTaskInfo;

					if (sub.taskId === targetTaskId) {
						matched.push(sub);
					} else {
						unmatched.push(items[i]);
					}

					i++;
				}

				result.push({ mainTask: null, subTasks: matched });
				items.splice(i, 0, ...unmatched);
			}
		}

		return result;
	}, [data]);

	return (
		<div
			ref={drop}
			className="w-full group relative border border-primary-gray-lightest p-1 pt-4 rounded-16 bg-surface flex flex-col min-h-[calc(100vh-200px)]"
		>
			<h3 className="w-full flex gap-2 text-primary-blue-dark text-[14px] font-medium px-3 mb-4 shrink-0">
				<img alt="logo" className="w-5 h-5" src={`${icon}`} />
				{ts(status)}
				<span className="border border-primary-gray-lightest bg-primary-gray-lightest rounded-[4px] w-5 h-5 flex items-center justify-center text-text text-[12px] font-medium">
					{data?.length ?? "0"}
				</span>
			</h3>

			{isLoading ? (
				<div className="w-full flex justify-center py-10 flex-1">
					<CircularProgress className="!w-12 !h-12" />
				</div>
			) : (
				<>
					<div
						ref={containerRef}
						className="flex-1 overflow-y-auto custom-scrollbar space-y-1 max-h-fit mb-2"
					>
						{isOverNewContainer && (
							<div
								className="w-full h-36 border border-dashed rounded-xl border-accent flex items-center justify-center opacity-0 transition-opacity duration-300 ease-in-out"
								style={{ opacity: isOverNewContainer ? 1 : 0 }}
							>
								<p className="text-[16px] text-subtle text-center">
									{ts("change-task-status")} &quot;{ts(status)}&quot;
								</p>
							</div>
						)}

						{data?.length ? (
							<>
								{groups.map((group, idx) => {
									const isStandaloneTask = group.mainTask && group.subTasks.length === 0;
									const isOrphanGroup = !group.mainTask;

									if (isStandaloneTask) {
										return (
											<ToDoItem
												key={group.mainTask!.id}
												data={group.mainTask!}
												isSubTask={false}
												showTaskDetails={showTaskDetails}
												users={users ?? null}
											/>
										);
									}

									const mainTaskId = group.mainTask?.id;
									const hasMainWithSubs = !!group.mainTask && group.subTasks.length > 0;
									const isCollapsed =
										hasMainWithSubs &&
										!(mainTaskId !== undefined && expandedTaskIds.has(mainTaskId));

									return (
										<div key={idx} className="rounded-xl bg-gray-700/10 p-2 space-y-1">
											{group.mainTask && (
												<ToDoItem
													data={group.mainTask}
													hasSubTasks={hasMainWithSubs}
													isSubTask={false}
													isSubTasksCollapsed={isCollapsed}
													showTaskDetails={showTaskDetails}
													subTasksCount={group.subTasks.length}
													users={users ?? null}
													onToggleSubTasks={() => {
														if (mainTaskId !== undefined) {
															toggleExpanded(mainTaskId);
														}
													}}
												/>
											)}
											{!isCollapsed &&
												group.subTasks.map((sub, subIdx) => (
													<ToDoItem
														key={sub.id}
														data={sub}
														isLastSubTask={subIdx === group.subTasks.length - 1}
														isOrphanGroup={isOrphanGroup}
														isSubTask={true}
														showParentLabel={subIdx === 0}
														showTaskDetails={() => {
															if (sub && sub.id) {
																showSubTaskDetails?.(sub.id);
															}
														}}
														users={users ?? null}
													/>
												))}
										</div>
									);
								})}
								{isBlocked ? (
									<p className="text-[14px] text-subtle text-center py-4 cursor-not-allowed">
										{draggingItem.subTaskId
											? ts("cannot-move-subtask-to-done")
											: ts("cannot-move-task-to-done")}
									</p>
								) : null}
							</>
						) : (
							<>
								{isBlocked ? (
									<p className="text-[14px] text-subtle text-center py-4 cursor-not-allowed">
										{draggingItem.subTaskId
											? ts("cannot-move-subtask-to-done")
											: ts("cannot-move-task-to-done")}
									</p>
								) : (
									<p className=" w-full text-[16px] text-subtle text-center py-4">
										{ts("list-empty")}
									</p>
								)}
							</>
						)}
					</div>

					<div
						className={`rounded-md w-full px-2 flex items-center cursor-pointer shrink-0 overflow-hidden transition-all ${
							isQuickAddShowing ? "py-1" : "h-[52px] hover:bg-black/5"
						}`}
						onClick={(e) => {
							e.stopPropagation();
							quickAddRef.current?.focus();
							setIsQuickAddShowing(true);
						}}
					>
						{!isQuickAddShowing ? (
							<div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-150">
								<Icon className="mr-1 text-gray-700" icon={faPlus} />
								<p className="text-gray-700">{t("createTask.createTask")}</p>
							</div>
						) : (
							<Controller
								control={control}
								name="title"
								render={({ field }) => (
									<TextField
										sx={{
											"& .MuiFilledInput-root": {
												backgroundColor: "white",
												"&:hover": { backgroundColor: "white" },
												"&.Mui-focused": { backgroundColor: "white" },
											},
										}}
										onKeyDown={(e) => {
											if (e.key === "Enter" && !e.shiftKey) {
												e.preventDefault();
												handleSubmit(onQuickAddSubmit)();
											}
										}}
										{...field}
										fullWidth
										multiline
										inputRef={quickAddRef}
										label={ts("onYourMind")}
										maxRows={2}
										minRows={8}
										size="small"
									/>
								)}
							/>
						)}
					</div>
				</>
			)}

			{droppedData && (
				<AssigneeModal
					data={droppedData}
					isOpen={isAssigneeModalOpen}
					setIsOpen={(val) => {
						setIsAssigneeModalOpen(val);
						if (!val) {
							setDroppedData(null);
						}
					}}
				/>
			)}
		</div>
	);
}
